/**
 * Area data registry. Each area exposes coastline, marks, lanes, racons,
 * and ferry templates; scenarios reference these via `metadata.area`,
 * `behaviour.laneId`, and `event.raconId` strings validated at load.
 */

import type { Area, LatLon } from "@radar-sim/schema";
import { SOLENT, SOLENT_TIDAL_ANOMALIES } from "./solent.js";
import type { AreaData } from "./types.js";
import {
  TIDAL_ANOMALIES,
  WEYMOUTH_PORTLAND,
  type TidalAnomaly,
} from "./weymouth-portland.js";
import { MORAY_FIRTH, MORAY_FIRTH_TIDAL_ANOMALIES } from "./moray-firth.js";
import { PLOCKTON, PLOCKTON_TIDAL_ANOMALIES } from "./plockton.js";
import { UK_GENERAL } from "./uk-general.js";

export type {
  AreaData,
  BuoyCategory,
  FerryTemplate,
  Light,
  LightColor,
  LightPattern,
  Mark,
  MarkType,
} from "./types.js";
export type { TidalAnomaly } from "./weymouth-portland.js";

/** Point-in-polygon test using the ray-casting algorithm. Returns
 *  true if `p` is inside the lat/lon polygon. Polygon edges are
 *  considered "outside" — a point exactly on an edge returns false
 *  (unstable but acceptable for tidal-drift bounds). */
export function isInsidePolygon(
  p: LatLon,
  polygon: ReadonlyArray<LatLon>,
): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    const intersect =
      a.lat > p.lat !== b.lat > p.lat &&
      p.lon < ((b.lon - a.lon) * (p.lat - a.lat)) / (b.lat - a.lat) + a.lon;
    if (intersect) inside = !inside;
  }
  return inside;
}
/**
 * Test whether the great-circle-approximated straight line between
 * `from` and `to` is occluded by a coastline polyline — i.e. a radar
 * pulse fired from `from` toward `to` would strike land before
 * reaching `to`. Used by the PPI to drop vessel paint for targets
 * sitting in terrain shadow behind an island.
 *
 * Implementation: 2D segment/segment intersection in lat/lon space.
 * Coastlines are simplified polylines, not closed polygons, so the
 * test is "does the from→to segment cross any coastline edge?". Both
 * endpoints are assumed to be in water; if the line crosses a
 * coastline edge it has passed through land, hence is occluded.
 */
export function isLineOccludedByCoast(
  from: LatLon,
  to: LatLon,
  coastline: ReadonlyArray<ReadonlyArray<LatLon>>,
): boolean {
  for (const ring of coastline) {
    for (let i = 0; i < ring.length - 1; i++) {
      const a = ring[i]!;
      const b = ring[i + 1]!;
      if (segmentsIntersect(from, to, a, b)) return true;
    }
  }
  return false;
}

function segmentsIntersect(
  p1: LatLon,
  p2: LatLon,
  p3: LatLon,
  p4: LatLon,
): boolean {
  const d1 = orient(p3, p4, p1);
  const d2 = orient(p3, p4, p2);
  const d3 = orient(p1, p2, p3);
  const d4 = orient(p1, p2, p4);
  if (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  ) {
    return true;
  }
  return false;
}

function orient(a: LatLon, b: LatLon, c: LatLon): number {
  return (b.lon - a.lon) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lon - a.lon);
}

export { describeLight, lightFrame, type LightFrame } from "./light.js";

const REGISTRY: Record<Area, AreaData> = {
  "solent": SOLENT,
  "weymouth-portland": WEYMOUTH_PORTLAND,
  "moray-firth": MORAY_FIRTH,
  "plockton": PLOCKTON,
  "uk-general": UK_GENERAL,
};

export function getAreaData(area: Area): AreaData {
  return REGISTRY[area];
}

export { SOLENT, WEYMOUTH_PORTLAND, MORAY_FIRTH, PLOCKTON, UK_GENERAL, TIDAL_ANOMALIES };

/**
 * Tidal anomalies are area-scoped. Phase 2 ships only Portland Race,
 * associated with the Weymouth/Portland area; the registry shape is
 * uniform so phase-3 additions plug in cleanly.
 */
export function getTidalAnomalies(area: Area): ReadonlyArray<TidalAnomaly> {
  if (area === "weymouth-portland") return TIDAL_ANOMALIES;
  if (area === "solent") return SOLENT_TIDAL_ANOMALIES;
  if (area === "moray-firth") return MORAY_FIRTH_TIDAL_ANOMALIES;
  if (area === "plockton") return PLOCKTON_TIDAL_ANOMALIES;
  return [];
}

/**
 * Adapter: turn an AreaData into the `AreaDataLookup` shape that the engine
 * behaviours expect. Decouples the engine package from any direct knowledge
 * of the area-data structure beyond the lane lookup.
 */
export function asAreaDataLookup(data: AreaData): {
  getLane(id: string): readonly LatLon[] | undefined;
} {
  return {
    getLane(id: string) {
      return data.lanes.get(id);
    },
  };
}

/**
 * Build a coastline predicate for the given area. Returns a function
 * that takes a position and returns true if it sits on land per the
 * area's coastline polylines (OSM-derived).
 *
 * Convention (set by the OSM fetcher):
 *   coastline[0]    — OPEN mainland polyline, clipped at bbox edges.
 *                     Land sits on one side of the line; per-area
 *                     `mainlandLandSide` selects which.
 *   coastline[1..N] — CLOSED island/landmass polygons. Point-in-polygon.
 *
 * The engine's coastline-router uses this predicate to deflect
 * tracked-vessel paths around land at scenario load time.
 */
export function makeIsOnLand(data: AreaData): (p: LatLon) => boolean {
  const closedRings = data.coastline.filter(isClosedRing);
  const openMainland = data.coastline.find((r) => !isClosedRing(r));
  const landSide = mainlandLandSide(data.area);
  const exception = mainlandException(data.area);
  // Close the open mainland into a polygon by tacking on two sentinel
  // vertices at a very-far-away latitude in the land direction. The
  // resulting closed polygon is point-in-polygon-testable just like an
  // island, and handles convoluted mainlands (peninsulas, headlands)
  // correctly without the per-longitude lat interpolation that would
  // double back on itself.
  const closedMainland =
    openMainland !== undefined && landSide !== "skip"
      ? closeMainland(openMainland, landSide)
      : undefined;
  return (p: LatLon): boolean => {
    if (exception !== undefined && exception(p)) return false;
    if (closedMainland !== undefined && isInsidePolygon(p, closedMainland)) {
      return true;
    }
    for (const ring of closedRings) {
      if (isInsidePolygon(p, ring)) return true;
    }
    return false;
  };
}

function closeMainland(
  ring: ReadonlyArray<LatLon>,
  side: "north" | "south",
): LatLon[] {
  const farLat = side === "north" ? 100 : -100;
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  return [
    ...ring,
    { lat: farLat, lon: last.lon },
    { lat: farLat, lon: first.lon },
  ];
}

export function isClosedRing(ring: ReadonlyArray<LatLon>): boolean {
  if (ring.length < 4) return false;
  const a = ring[0]!;
  const b = ring[ring.length - 1]!;
  return Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lon - b.lon) < 1e-6;
}

/**
 * Which side of the mainland polyline (in lat) is land. Determined
 * by area geography. "skip" disables the open-mainland test entirely
 * for areas where the convention doesn't hold (Plockton's heavily
 * fjorded coast, the UK-general low-detail backdrop). Exposed so the
 * chart land-fill can match the predicate.
 */
export function mainlandLandSide(area: Area): "north" | "south" | "skip" {
  switch (area) {
    case "solent":
      return "north"; // Hampshire is north of the polyline
    case "weymouth-portland":
      return "north"; // Dorset mainland is north of the polyline
    case "moray-firth":
      return "south"; // Inverness/Buckie shore is south of the firth
    case "plockton":
      return "skip"; // West-Highland fjords — rely on closed-ring islands
    case "uk-general":
      return "skip"; // Low-detail backdrop only
  }
}

function mainlandException(
  area: Area,
): ((p: LatLon) => boolean) | undefined {
  if (area === "solent") {
    // Southampton Water carves into the Hampshire mainland north of
    // Calshot. The exception keeps Red Funnel ferries up to East
    // Cowes from being deflected off course; bound it tightly so it
    // doesn't accidentally turn the central IOW (Newport, lon -1.29
    // lat 50.7) back into water.
    return (p) =>
      p.lon >= -1.43 && p.lon <= -1.3 && p.lat >= 50.81 && p.lat <= 50.92;
  }
  return undefined;
}

